// Időkapu-foglaló · iter147 — Plant-side booking search / filter.
//
// dock_admin-only page at /dock-scheduling/search. Operational tool for the
// plant to find bookings by reference / plate / provider / TO / date range
// / dock / direction / arrival status. Tenant-scoped by API.

import React, { useState, useMemo } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { Search, RotateCcw, Filter, Ticket } from "lucide-react";
import SlotTicket, { PrintButton } from "./DockSlotTicket";
import ArrivalPill from "./DockArrivalPill";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dock-scheduling`;
const BLUE = "#185FA5";

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});
const syne = (size, weight) => ({ fontFamily: "Syne, sans-serif", fontSize: size, fontWeight: weight || 700 });

const inputStyle = {
  background: "var(--bg-input, #0a0b12)",
  border: "1px solid rgba(148,163,184,0.30)",
  color: "var(--text, #f0f2f8)",
  ...mono(12),
};

function useDocks() {
  return useQuery({
    queryKey: ["ds-search-docks"],
    queryFn: () => axios.get(`${API}/docks`).then(r => r.data.items || []),
  });
}

export default function AdminDockSearch() {
  const [filters, setFilters] = useState({
    ref: "", plate: "", provider: "", to_szam: "",
    direction: "", dock_id: "", arrival_status: "", status: "",
    date_from: "", date_to: "",
  });
  const [applied, setApplied] = useState(null);
  const [ticket, setTicket] = useState(null);

  const { data: docks = [] } = useDocks();

  const results = useQuery({
    queryKey: ["ds-search", applied],
    queryFn: () => axios.get(`${API}/admin/bookings/search`, { params: applied }).then(r => r.data),
    enabled: !!applied,
  });

  const apply = () => {
    const clean = {};
    for (const [k, v] of Object.entries(filters)) {
      if (v && String(v).trim()) clean[k] = String(v).trim();
    }
    setApplied(clean);
  };

  const reset = () => {
    setFilters({
      ref: "", plate: "", provider: "", to_szam: "",
      direction: "", dock_id: "", arrival_status: "", status: "",
      date_from: "", date_to: "",
    });
    setApplied(null);
  };

  const rows = results.data?.items || [];

  return (
    <div data-testid="ds-booking-search" className="p-1">
      <header className="mb-4">
        <h1 style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: "clamp(22px, 3vw, 30px)", color: "var(--text, #f0f2f8)", letterSpacing: "-0.5px" }} data-testid="view-title">
          <Search size={26} color={BLUE} style={{ display: "inline", verticalAlign: -4, marginRight: 10 }} />
          Foglalás keresés
        </h1>
        <div className="mt-1.5" style={mono(11, "var(--text-dim, #c5cad8)")}>
          Szűrés referencia · rendszám · szolgáltató · TO · dokk · irány · állapot · dátum szerint
        </div>
      </header>

      {/* Filter form */}
      <section className="mb-4 rounded-md p-3" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(148,163,184,0.15)" }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { key: "ref", label: "REFERENCIA (IK-)", testId: "ds-search-ref" },
            { key: "plate", label: "RENDSZÁM", testId: "ds-search-plate" },
            { key: "provider", label: "SZOLGÁLTATÓ", testId: "ds-search-provider" },
            { key: "to_szam", label: "TO SZÁM", testId: "ds-search-to" },
          ].map(f => (
            <label key={f.key} className="block">
              <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>{f.label}</div>
              <input
                data-testid={f.testId}
                value={filters[f.key]}
                onChange={(e) => setFilters(s => ({ ...s, [f.key]: e.target.value }))}
                className="px-2 py-1.5 rounded w-full mt-1"
                style={inputStyle}
              />
            </label>
          ))}

          <label className="block">
            <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>IRÁNY</div>
            <select
              data-testid="ds-search-direction"
              value={filters.direction}
              onChange={(e) => setFilters(s => ({ ...s, direction: e.target.value }))}
              className="px-2 py-1.5 rounded w-full mt-1"
              style={inputStyle}
            >
              <option value="">Bármelyik</option>
              <option value="inbound">Bejövő</option>
              <option value="outbound">Kimenő</option>
            </select>
          </label>

          <label className="block">
            <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>DOKK</div>
            <select
              data-testid="ds-search-dock"
              value={filters.dock_id}
              onChange={(e) => setFilters(s => ({ ...s, dock_id: e.target.value }))}
              className="px-2 py-1.5 rounded w-full mt-1"
              style={inputStyle}
            >
              <option value="">Bármelyik</option>
              {docks.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>

          <label className="block">
            <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>ÉRKEZÉS STÁTUSZ</div>
            <select
              data-testid="ds-search-status"
              value={filters.arrival_status}
              onChange={(e) => setFilters(s => ({ ...s, arrival_status: e.target.value }))}
              className="px-2 py-1.5 rounded w-full mt-1"
              style={inputStyle}
            >
              <option value="">Bármelyik</option>
              <option value="pending">Nem érkezett</option>
              <option value="arrived">Érkezett</option>
              <option value="late">Késve érkezett</option>
              <option value="no_show">Nem jelent meg</option>
            </select>
          </label>

          <label className="block">
            <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>FOGLALÁS STÁTUSZ</div>
            <select
              value={filters.status}
              onChange={(e) => setFilters(s => ({ ...s, status: e.target.value }))}
              className="px-2 py-1.5 rounded w-full mt-1"
              style={inputStyle}
            >
              <option value="">Bármelyik</option>
              <option value="booked">Foglalt</option>
              <option value="cancelled">Lemondva</option>
            </select>
          </label>

          <label className="block" data-testid="ds-search-daterange">
            <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>DÁTUMTÓL</div>
            <input
              type="date"
              data-testid="ds-search-date-from"
              value={filters.date_from}
              onChange={(e) => setFilters(s => ({ ...s, date_from: e.target.value }))}
              className="px-2 py-1.5 rounded w-full mt-1"
              style={inputStyle}
            />
          </label>

          <label className="block">
            <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>DÁTUMIG</div>
            <input
              type="date"
              data-testid="ds-search-date-to"
              value={filters.date_to}
              onChange={(e) => setFilters(s => ({ ...s, date_to: e.target.value }))}
              className="px-2 py-1.5 rounded w-full mt-1"
              style={inputStyle}
            />
          </label>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            data-testid="ds-search-apply"
            onClick={apply}
            className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
            style={{ background: BLUE, color: "#fff", border: 0, cursor: "pointer", ...mono(11, "#fff", 700, "0.4px") }}
          >
            <Filter size={14} /> KERESÉS
          </button>
          <button
            type="button"
            data-testid="ds-search-reset"
            onClick={reset}
            className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
            style={{ background: "transparent", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", cursor: "pointer", ...mono(11, undefined, 700, "0.4px") }}
          >
            <RotateCcw size={13} /> ÜRÍTÉS
          </button>
          {applied && (
            <div className="flex-1 text-right" style={mono(10, "var(--dimmer)")}>
              {rows.length} találat
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      {applied && (
        <div className="rounded-md" data-testid="ds-search-results"
          style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(148,163,184,0.15)" }}
        >
          {results.isLoading && <div className="p-4" style={mono(11, "var(--dimmer)")}>Betöltés…</div>}
          {results.isError && <div className="p-4" style={mono(11, "#f87171")}>Hiba: {results.error?.response?.data?.detail || "Ismeretlen"}</div>}
          {!results.isLoading && rows.length === 0 && (
            <div className="p-4" style={mono(11, "var(--dimmer)")}>Nincs találat.</div>
          )}
          {rows.length > 0 && (
            <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.25)" }}>
                  {["IDŐPONT", "REFERENCIA", "VONAL", "SZOLGÁLTATÓ", "RENDSZÁM", "DOKK", "IRÁNY", "ÉRKEZÉS", "STÁTUSZ"].map(h => (
                    <th key={h} className="text-left px-3 py-2" style={mono(10, "var(--dimmer)", 700, "0.4px")}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(b => {
                  const dock = docks.find(d => d.id === b.dock_id);
                  return (
                    <tr key={b.id}
                      data-testid={`ds-search-row-${b.id}`}
                      onClick={() => setTicket({ ...b, dock_name: dock?.name })}
                      style={{ borderBottom: "1px solid rgba(148,163,184,0.10)", cursor: "pointer", opacity: b.status === "cancelled" ? 0.55 : 1 }}
                    >
                      <td className="px-3 py-2">
                        <div style={mono(12, undefined, 700)}>{(b.slot_start || "").slice(0, 10)}</div>
                        <div style={mono(11, BLUE, 700, "0.4px")}>{b.slot_start_local}–{b.slot_end_local}</div>
                      </td>
                      <td className="px-3 py-2" style={mono(11, BLUE, 800, "0.6px")}>{b.reference || "—"}</td>
                      <td className="px-3 py-2" style={mono(12)}>{b.vonal || "—"}</td>
                      <td className="px-3 py-2" style={mono(12)}>{b.szolgaltato_neve || "—"}</td>
                      <td className="px-3 py-2" style={mono(12)}>{b.rendszam || "—"}</td>
                      <td className="px-3 py-2" style={mono(12)}>{dock?.name || "—"}</td>
                      <td className="px-3 py-2" style={mono(12)}>{b.direction === "inbound" ? "Bejövő" : b.direction === "outbound" ? "Kimenő" : "—"}</td>
                      <td className="px-3 py-2"><ArrivalPill status={b.arrival_status} testId={`ds-search-arrival-${b.id}`} /></td>
                      <td className="px-3 py-2">
                        <span style={{
                          padding: "2px 8px", borderRadius: 999,
                          ...mono(9, b.status === "cancelled" ? "#f87171" : "#22c55e", 700, "0.4px"),
                          background: b.status === "cancelled" ? "rgba(248,113,113,0.10)" : "rgba(34,197,94,0.10)",
                          border: `1px solid ${b.status === "cancelled" ? "rgba(248,113,113,0.35)" : "rgba(34,197,94,0.35)"}`,
                          textTransform: "uppercase",
                        }}>{b.status === "cancelled" ? "Lemondva" : "Foglalt"}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {ticket && (
        <div
          data-testid="ds-search-ticket-modal"
          style={{
            position: "fixed", inset: 0, zIndex: 60,
            background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
          }}
          onClick={() => setTicket(null)}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, width: "100%" }}>
            <SlotTicket booking={ticket} />
            <div className="mt-3 flex items-center gap-2 justify-center">
              <PrintButton />
              <button type="button" onClick={() => setTicket(null)}
                className="px-3 py-1.5 rounded"
                style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.30)", color: "#fff", cursor: "pointer", ...mono(11, "#fff", 700, "0.4px") }}
              >Bezárás</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
